import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';

import { ApiService } from '@frontend/sports/common/api-utils';
import { PrettyUrlsConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { CompetitionRouteService } from '@frontend/sports/competition-list/feature';
import { MasterdataApiService } from '@frontend/sports/content-distribution/feature';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { SportUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { assign, isArray, isEqual, pickBy } from 'lodash-es';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';

import { CompetitionListSeoService } from '../competition-list/competition-list-seo.service';
import { SeoContentApiRequest, SeoContentApiResponse, SeoRoute } from './seo.models';

interface SeoContentRequest {
    filter?: string;
    lobby?: boolean;
    live?: boolean;
    virtual?: boolean;
    highlights?: boolean;
    allSports?: boolean;
    sport?: number;
    region?: number | string;
    league?: number;
    event?: string;
    video?: boolean;
    conference?: number;
    virtualCompetitionId?: number;
    virtualCompetitionGroupId?: number;
    esportsLobby?: boolean;
    esportsHighlights?: boolean;
    multiSportsLobby?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeoContentService {
    private competitionRoute = inject(CompetitionRouteService);
    private api = inject(ApiService);
    private urlConfig = inject(PrettyUrlsConfig);
    private competitionListSeoService = inject(CompetitionListSeoService);
    private masterDataApiService = inject(MasterdataApiService);

    private lastRequestApiParams: SeoContentApiRequest | null = null;
    private lastResponse: SeoContentApiResponse | null = null;
    private readonly logger: SportsRemoteLogger;

    constructor() {
        const loggerFactory = inject(LoggerFactory);

        this.logger = loggerFactory.getLogger('SeoContentService');
    }

    getForEventDetails(request: SeoContentRequest): Observable<SeoContentApiResponse | undefined> {
        return this.performApiRequest(request);
    }

    getSeoContent(routeParams: Params, data: SeoRoute): Observable<SeoContentApiResponse | undefined> {
        const competitionData = this.competitionRoute.params();
        const league = competitionData.league;
        const routeData = assign(competitionData, routeParams);

        if (!routeData) {
            return of(undefined);
        }

        return this.getLeagueId(routeData, league || routeData.league).pipe(
            switchMap((leagueId) => {
                return this.performApiRequest({
                    filter: this.getSeoFilter(routeData),
                    lobby: data.sportsLobby,
                    live: data.live,
                    virtual: data.virtual,
                    highlights: data.liveHighlights,
                    allSports: data.sportsList,
                    sport: routeData.sport,
                    region: routeData.region || routeData.tournament,
                    league: leagueId,
                    video: data.liveVideo,
                    conference: routeData.conference,
                    virtualCompetitionId: routeData.isVirtual ? routeParams.league : null,
                    virtualCompetitionGroupId: routeData.isVirtual ? routeParams.virtualCompetitionGroup : null,
                    esportsLobby: data.esportsLobby,
                    esportsHighlights: data.esportsHighlights,
                    multiSportsLobby: data.multiSportsLobby,
                }).pipe(
                    tap((response) => {
                        if (response) {
                            const fixtureContext: (string | undefined)[] = [
                                this.urlConfig.translations.betting,
                                this.urlConfig.translations.conferences,
                                this.urlConfig.translations.after2days,
                                this.urlConfig.translations.after3days,
                                this.urlConfig.translations.in30minutes,
                                this.urlConfig.translations.in60minutes,
                                this.urlConfig.translations.in180minutes,
                                this.urlConfig.translations.today,
                                this.urlConfig.translations.tomorrow,
                            ];

                            response.h1Included = fixtureContext.includes(competitionData.context) && !!competitionData.region;
                        }
                    }),
                );
            }),
        );
    }

    private getLeagueId(routeData: CompetitionRoute & Params, leagueId: number | number[] | undefined): Observable<number | undefined> {
        const league = isArray(leagueId) ? undefined : leagueId;
        if (!routeData.isVirtual) {
            return of(league);
        }
        const realCompetitionId = this.competitionListSeoService.getRealCompetitionId();
        if (realCompetitionId) {
            return of(realCompetitionId);
        }
        if (routeData.sport && league) {
            return this.masterDataApiService
                .getVirtualCompetitionInfo({
                    competitionId: league,
                    sportId: routeData.sport,
                    virtualCompetitionGroupId: routeData.virtualCompetitionGroup || 0,
                    virtualCompetitionId: league,
                })
                .pipe(
                    map(
                        (virtualCompetitionInfo) =>
                            virtualCompetitionInfo?.virtualCompetitionGroup?.competitionIds[0] ||
                            virtualCompetitionInfo?.virtualCompetition?.competitionIds[0],
                    ),
                    catchError((err) => {
                        if (err.status) {
                            this.logger.error(err, 'Error fetching virtual competition info');
                        }

                        return of(undefined);
                    }),
                );
        }

        return of(league);
    }

    private performApiRequest(request: SeoContentRequest): Observable<SeoContentApiResponse | undefined> {
        const apiParams: SeoContentApiRequest = pickBy(request, (prop) => prop != null && prop !== false && prop !== '');

        if (this.lastResponse != null && isEqual(apiParams, this.lastRequestApiParams)) {
            return of(this.lastResponse);
        }

        return this.api.get<SeoContentApiResponse>('seo/tags', apiParams).pipe(
            tap((response) => {
                this.lastRequestApiParams = apiParams;
                this.lastResponse = response;
            }),
            catchError((err) => {
                // check if request got cancelled (happens e.g. if the user quickly navigates to another page or if the signal was lost) and only log exception if it's a real error
                if (err.status) {
                    this.logger.error(err, 'Error getting SEO tags');
                }

                return of(undefined);
            }),
        );
    }

    private getSeoFilter(routeData: CompetitionRoute & Params): string | undefined {
        const contextKey = (input: string | undefined) => Object.entries(this.urlConfig.translations).find(([_, value]) => value === input)?.[0];

        const subContext = contextKey(routeData.subContext);

        return subContext === SportUrlParam.Competitions ? subContext : contextKey(routeData.context);
    }
}
